import React from 'react';
import {
  Popover,
  Row,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import StackedDateSelector from '../../../common/datepicker/StackedDateSelector';
import { currencyParser, currencyFormatter } from '../../../helpers/inputParsers';
import OnTraccrNumberInput from '../../../common/inputs/OnTraccrNumberInput';
import {
  computeQuantityRemaining,
  getPriceFloat,
  hasMarkup,
  isQuantityOverRemaining,
} from '../../../materials/materialsHelpers';
import { isNullOrUndefined } from '../../../helpers/helpers';
import { priceToCostMap, priceToMarkupMap } from '../../forms.constants';

export default {};

export const getColumnTitle = ({
  requiredColumns,
  title,
}) => <div className={requiredColumns && 'form-required-field'}>{title}</div>;

export const getUpdatedDateColumn = ({
  title = 'Date Updated',
  dataIndex = 'updatedDate',
  width = 250,
  requiredColumns = false,
  onChange,
  isDisplay = false,
}) => ({
  title: <div className={requiredColumns && 'form-required-field'}>{title}</div>,
  dataIndex,
  width,
  render: (updatedDates, record) => {
    const safeDatesArray = typeof updatedDates === 'string' ? updatedDates.split(', ') : [];

    return (
      <StackedDateSelector
        value={safeDatesArray}
        onChange={(val) => onChange(val, record)}
        isDisplay={isDisplay}
      />
    );
  },
});

export const getCostColumn = ({
  title,
  dataIndex,
  width = 150,
  requiredColumns = false,
  onChange,
  isDisplay = false,
  precision = 2,
  step = 0.01,
  formatter = currencyFormatter,
}) => ({
  title: getColumnTitle({ requiredColumns, title }),
  dataIndex,
  width,
  render: (cost, record) => {
    if (isDisplay) {
      if (!cost) return '$0';
      return formatter(Number.parseFloat(cost));
    }
    return (
      <OnTraccrNumberInput
        key={record.id}
        formatter={formatter}
        parser={currencyParser}
        step={step}
        precision={precision}
        onChange={(val) => onChange(val, record)}
        defaultValue={cost}
        {...cost ? { value: cost } : {}}
      />
    );
  },
});

export const getPriceColumn = ({
  title,
  dataIndex,
  width = 100,
  requiredColumns = false,
  onChange,
  isDisplay = false,
  discount,
}) => ({
  title: getColumnTitle({ requiredColumns, title }),
  dataIndex,
  width,
  render: (price, record) => {
    if (isDisplay || record.isAssembly) {
      if (!price) return '$0';
      return currencyFormatter(Number.parseFloat(price));
    }
    const costKey = priceToCostMap[dataIndex];
    const markupKey = priceToMarkupMap[dataIndex];

    const initialPrice = record[costKey] && hasMarkup(record[markupKey])
      ? getPriceFloat(record[costKey], record[markupKey], discount)
      : null;
    return (
      <OnTraccrNumberInput
        key={record.id}
        formatter={currencyFormatter}
        parser={currencyParser}
        step={0.01}
        precision={2}
        onChange={(val) => onChange(val, record)}
        defaultValue={initialPrice}
            // eslint-disable-next-line react/jsx-props-no-spreading
        {...price ? { value: price } : {}}
      />
    );
  },
});

export const getQuantityRemainingColumn = ({
  title,
  quantityRemainingMode = 'live',
}) => ({
  title,
  dataIndex: 'quantityRemaining',
  width: 140,
  render: (quantityRemaining, record) => {
    const { quantity, quantityAllocated } = record;

    const displayValue = computeQuantityRemaining({
      quantity,
      quantityAllocated,
      quantityRemaining,
      quantityRemainingMode,
    });

    if (isNullOrUndefined(displayValue)) {
      return null;
    }

    const isOverRemaining = isQuantityOverRemaining({
      quantity,
      quantityRemaining: displayValue,
      quantityRemainingMode,
    });

    if (!isOverRemaining) {
      return displayValue;
    }

    return (
      <Row align="middle" style={{ gap: 6, color: 'red' }}>
        <Popover
          content={quantityRemainingMode === 'static' ? 'Quantity exceeds quantity remaining' : 'Quantity exceeds allocation'}
          placement="top"
        >
          <ExclamationCircleOutlined style={{ cursor: 'help' }} />
        </Popover>
        <span>{displayValue}</span>
      </Row>
    );
  },
});
